import './process-pricing.css'

import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PageHeader, PieChartComponent, Spinner, Text } from '../../../Components'
import { formatMoney3Decimals } from '../../../Services/Functions'
import { useCQuery } from '../../../Services/QueryCache'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { Provider } from './Provider'

const language = cap.language.state || 'IT'
const t = translations[language]

const columnStyle = { textAlign: 'center' }
const colors = [
  '#2563eb',
  '#14a06b',
  '#d97706',
  '#f97316',
  '#d92d2d',
  '#3b82f6',
  '#22c55e',
  '#ec4899',
  '#eab308',
  '#06b6d4'
]

/**
 * Pagina pricing LLM per conversazione con filtro provider e grafico ripartizione.
 * @returns {JSX.Element} Componente pricing conversazione
 */
export const ConversationPricing = () => {
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const { data, isSuccess } = useCQuery(['pricingPerConversation', conversationId])

  const [providers, setProviders] = useState([])

  const totalRow = useMemo(() => data?.find((row) => row.unit === 'Totale') || data?.[0], [data])

  useEffect(() => {
    if (!isSuccess || !totalRow?.tokenCount?.length) return
    setProviders(
      totalRow.tokenCount.map(({ provider, company }) => ({
        provider,
        disabled: false,
        company
      }))
    )
  }, [isSuccess, totalRow])

  const totalCostPerProvider = useMemo(() => {
    if (!totalRow?.tokenCount?.length) return []
    const disabledProviders = new Set(providers.filter((p) => p.disabled).map((p) => p.provider))
    return totalRow.tokenCount
      .filter(
        ({ provider, providerTotal }) =>
          !disabledProviders.has(provider) && Number(providerTotal) > 0
      )
      .map(({ provider, providerTotal }) => ({
        name: provider,
        value: providerTotal
      }))
  }, [totalRow, providers])

  if (!isSuccess) return <Spinner />

  const companies = [...new Set(data?.[0]?.tokenCount?.map(({ company }) => company) || [])]
  // La valuta è quella della riga Totale: il legacy conta in euro, il motore agentico
  // nella valuta del listino del modello dichiarato dal tenant.
  const valuta = totalRow?.valuta
  const importo = (value) => formatMoney3Decimals(value, valuta)

  return (
    <div className="process-pricing-page">
      <PageHeader
        title={`${t.pricing} · ${t.conversation} #${conversationId}`}
        subtitle={t.costoLlmProcessi}
        actions={
          <button
            type="button"
            className="nbtn"
            onClick={() => navigate('/conversations/' + conversationId)}
          >
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.tornaIndietro}
          </button>
        }
      />

      <div className="ncard" style={{ marginBottom: 14 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-filter" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.filtraProvider}</h3>
            <div className="ncard__sub">{t.selezionaProviderVisualizzare}</div>
          </div>
        </div>
        <div className="ncard__body">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {companies.map((company, i) => (
              <Provider key={i} providers={[providers, setProviders]} company={company} />
            ))}
          </div>
        </div>
      </div>

      <div className="process-pricing-grid">
        <div className="ncard process-pricing-table-wrap" style={{ padding: 0 }}>
          <div className="ncard__head">
            <div className="ncard__ico">
              <i className="pi pi-euro" style={{ fontSize: 16 }} />
            </div>
            <div>
              <h3 className="ncard__title">{t.dettaglioCostiUnit}</h3>
            </div>
          </div>
          <DataTable
            value={data}
            emptyMessage={t.nessunCostoVisualizzare}
            paginator
            rows={10}
            className="TableSignatures ntable-datatable"
            style={{ maxWidth: '100%' }}
            responsiveLayout="scroll"
            resizableColumns
            columnResizeMode="expand"
            filterDisplay="row"
          >
            <Column
              field="unit"
              header={t.unit}
              body={(row) => (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{row.unit}</span>
              )}
              {...columnStyle}
            />
            <Column
              field="processId"
              header={t.processo}
              sortable
              body={(row) => <span className="ntable__num">#{row.processId}</span>}
              {...columnStyle}
            />
            <Column
              sortable
              field="total"
              header={t.totale}
              body={(row) => (
                <span className="ntable__num" style={{ fontWeight: 600 }}>
                  {importo(row?.total)}
                </span>
              )}
              {...columnStyle}
            />
            {providers?.map(
              (provider, i) =>
                !provider.disabled && (
                  <Column
                    field={'tokenCount.' + i + '.tokenCount'}
                    header={t.pfToken + provider.provider}
                    key={i}
                    sortable
                    body={(row) => (
                      <span className="ntable__num">{row?.tokenCount?.[i]?.tokenCount || ''}</span>
                    )}
                    {...columnStyle}
                  />
                )
            )}
            {providers?.map(
              (provider, i) =>
                !provider.disabled && (
                  <Column
                    sortable
                    field={'tokenCount.' + i + '.providerTotal'}
                    header={`${t.totale} ${provider.provider}`}
                    key={`total-${i}`}
                    body={(row) => (
                      <span className="ntable__num">
                        {importo(row?.tokenCount?.[i]?.providerTotal)}
                      </span>
                    )}
                    {...columnStyle}
                  />
                )
            )}
          </DataTable>
        </div>

        <div className="ncard">
          <div className="ncard__head">
            <div className="ncard__ico">
              <i className="pi pi-chart-pie" style={{ fontSize: 16 }} />
            </div>
            <div>
              <h3 className="ncard__title">{t.ripartizioneCosti}</h3>
              <div className="ncard__sub">{t.perProvider}</div>
            </div>
          </div>
          <div className="ncard__body process-pricing-chart">
            {totalCostPerProvider.length ? (
              <PieChartComponent
                legendProps={{ layout: 'vertical', align: 'left', verticalAlign: 'middle' }}
                legend
                data={totalCostPerProvider}
                dataKey="value"
                colors={colors}
                innerRadius="0%"
                outerRadius="100%"
                paddingAngle="0"
                tooltipProps={{
                  content: ({ payload: [{ payload: { name = '', value } = {} } = {}] = [] }) => (
                    <div>
                      <Text value={name} />
                      <Text value={`${t.totale}: ${importo(value)}`} />
                    </div>
                  )
                }}
              />
            ) : (
              <Text value={t.nessunCostoVisualizzare} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
